import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Lock,
  Play,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import { 
  useDisruptionProgress, 
  useResolveNext 
} from '@/lib/disruptions/use-disruptions';
import type {
  DisruptionProgress,
  ResolutionOp,
  ResolutionState,
} from '@/lib/disruptions/types';
import { 
  stepIcon, 
  stepLabel, 
  stepSubject,
} from '../resolution-steps';
import { RebookingProgressDialog } from '../rebooking-progress-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface DisruptionSmartHeroProps {
  disruptionUrn: string;
  state: ResolutionState;
}

const COMMIT_OPS: ReadonlySet<ResolutionOp> = new Set<ResolutionOp>([
  'tickets',
  'acceptance',
  'contain',
]);

const DIALOG_OP: Partial<Record<ResolutionOp, 'rebooking' | 'allocation'>> = {
  rebooking: 'rebooking',
  hotels: 'allocation',
};

export function DisruptionSmartHero({ disruptionUrn, state }: DisruptionSmartHeroProps) {
  const { t } = useTranslation('tower');
  const resolve = useResolveNext(disruptionUrn);
  const { data: live } = useDisruptionProgress(disruptionUrn);

  const [confirmOp, setConfirmOp] = useState<ResolutionOp | null>(null);
  const [dialogOp, setDialogOp] = useState<'rebooking' | 'allocation' | null>(null);

  const next = state.nextStep;
  const nextOp = next.op;
  const activeRun = live?.activeRun ?? state.progress.activeRun ?? null;
  const runInFlight = !!activeRun;
  const isContained = state.steps.find((s) => s.op === 'contain')?.status === 'DONE';
  const mode = next.mode ?? (nextOp === 'none' ? 'done' : 'start');
  const actionable = (next.actionable ?? nextOp !== 'none') && !runInFlight && !resolve.isPending;

  const NextIcon = nextOp !== 'none' ? stepIcon(nextOp) : CheckCircle2;

  function fire(op: ResolutionOp): void {
    resolve.mutate(
      { dryRun: false, expectedOp: op },
      {
        onSuccess: (res) => {
          setConfirmOp(null);
          const dialog = DIALOG_OP[op];
          if (res.startedOp === op && dialog) setDialogOp(dialog);
        },
      },
    );
  }

  function handlePrimaryAction(): void {
    if (nextOp === 'none' || !actionable) return;
    if (COMMIT_OPS.has(nextOp)) {
      setConfirmOp(nextOp);
      return;
    }
    fire(nextOp);
  }

  const lastDurable: 'rebooking' | 'allocation' | null = useMemo(() => {
    if (activeRun?.op === 'rebooking' || activeRun?.op === 'allocation') return activeRun.op;
    if (live?.allocationRun) return 'allocation';
    if (live?.runStatus && live.runStatus !== 'NONE') return 'rebooking';
    return null;
  }, [activeRun?.op, live?.allocationRun, live?.runStatus]);

  return (
    <Card className={cn(
      "relative overflow-hidden border shadow-sm transition-all duration-300",
      isContained 
        ? "border-emerald-500/40 bg-gradient-to-r from-emerald-500/5 via-card to-card" 
        : runInFlight
          ? "border-sky-500/40 bg-gradient-to-r from-sky-500/5 via-card to-card"
          : "border-primary/30 bg-gradient-to-r from-primary/[0.04] via-card to-card"
    )}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          
          {/* Left Side: Intelligent Recommendation Details */}
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2">
              <Badge variant="neutral" className={cn(
                "px-2 py-0.5 text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5",
                isContained 
                  ? "border-emerald-500/50 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40"
                  : runInFlight
                    ? "border-sky-500/50 text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 animate-pulse"
                    : "border-primary/40 text-primary bg-primary/10"
              )}>
                {isContained ? (
                  <>
                    <CheckCircle2 className="h-3 w-3" />
                    <span>{t('alt.hero.resolved')}</span>
                  </>
                ) : runInFlight ? (
                  <>
                    <Activity className="h-3 w-3 animate-spin" />
                    <span>{t('alt.hero.executing')}</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3 w-3" />
                    <span>{t('alt.hero.recommended')}</span>
                  </>
                )}
              </Badge>

              {next.mode === 'blocked' ? (
                <Badge variant="error" className="text-[11px] gap-1">
                  <Lock className="h-3 w-3" /> {t('alt.hero.blocked')}
                </Badge>
              ) : null}
            </div>

            <div className="space-y-1">
              <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
                <NextIcon className="h-5 w-5 text-primary" />
                {isContained
                  ? t('alt.hero.containedHeadline')
                  : runInFlight
                    ? t('alt.hero.processing', {
                        subject: activeRun ? stepSubject(activeRun.op) : t('alt.hero.operation'),
                      })
                    : (next.cta ??
                      (nextOp !== 'none' ? stepLabel(nextOp) : t('alt.hero.noPending')))}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {isContained
                  ? t('alt.hero.containedBody')
                  : next.reason || t('alt.hero.defaultReason')}
              </p>
            </div>
          </div>

          {/* Right Side: High-Impact Action Triggers */}
          <div className="flex flex-wrap items-center gap-2.5 shrink-0">
            {lastDurable ? (
              <Button
                variant="outline"
                size="default"
                onClick={() => setDialogOp(lastDurable)}
                className="gap-2 text-xs font-semibold shadow-xs"
              >
                <Activity className="h-4 w-4 text-sky-500 animate-pulse" />
                <span>{t('alt.hero.liveTelemetry')}</span>
              </Button>
            ) : null}

            {!isContained && nextOp !== 'none' ? (
              <Button
                size="lg"
                onClick={handlePrimaryAction}
                disabled={!actionable}
                className={cn(
                  "gap-2.5 px-6 font-semibold shadow-md transition-all duration-200",
                  mode === 'retry' && "bg-amber-600 hover:bg-amber-700",
                  runInFlight && "bg-sky-600 hover:bg-sky-700"
                )}
              >
                {resolve.isPending || runInFlight ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 fill-current" />
                )}
                <span>
                  {runInFlight ? t('alt.hero.processingShort') : (next.cta ?? stepLabel(nextOp))}
                </span>
                <ArrowRight className="h-4 w-4 opacity-70" />
              </Button>
            ) : null}
          </div>

        </div>

        {/* Live Execution Mini-Progress Bar (When active) */}
        {activeRun ? (
          <div className="mt-4 pt-3 border-t border-border/60">
            <LiveTelemetryStrip live={live} state={state} />
          </div>
        ) : null}
      </CardContent>

      {/* Confirmation modal for committing ops */}
      <ConfirmDialog
        op={confirmOp}
        state={state}
        isFiring={resolve.isPending}
        onConfirm={() => confirmOp && fire(confirmOp)}
        onCancel={() => setConfirmOp(null)}
      />

      {/* Realtime progress drawer / dialog */}
      {dialogOp ? (
        <RebookingProgressDialog
          disruptionUrn={disruptionUrn}
          open={!!dialogOp}
          onOpenChange={(open) => (open ? undefined : setDialogOp(null))}
          op={dialogOp}
        />
      ) : null}
    </Card>
  );
}

function LiveTelemetryStrip({
  live,
  state,
}: {
  live: DisruptionProgress | undefined;
  state: ResolutionState;
}) {
  const { t } = useTranslation('tower');
  const activeRun = live?.activeRun ?? state.progress.activeRun ?? null;
  let done = 0;
  let total = 0;
  let unit = t('alt.telemetry.unitGroups');

  if (activeRun?.op === 'rebooking') {
    done = live?.processedGroups ?? 0;
    total = live?.plannedGroups ?? 0;
  } else if (activeRun?.op === 'allocation') {
    const a = live?.allocationRun;
    done = a?.processedGroups ?? 0;
    total = a?.totalGroups ?? 0;
  } else {
    done = activeRun?.progress?.done ?? 0;
    total = activeRun?.progress?.total ?? 0;
    unit = t('alt.telemetry.unitCases');
  }

  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  if (!activeRun) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-xs font-semibold">
        <span className="text-foreground flex items-center gap-1.5">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-500" />
          <span>{t('alt.telemetry.progress', { subject: stepSubject(activeRun.op) })}</span>
        </span>
        <span className="font-mono tabular-nums text-muted-foreground">
          {done}/{total} {unit} ({pct}%)
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div 
          className="h-full rounded-full bg-sky-500 transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function ConfirmDialog({
  op,
  state,
  isFiring,
  onConfirm,
  onCancel,
}: {
  op: ResolutionOp | null;
  state: ResolutionState;
  isFiring: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('tower');
  const open = !!op;
  const g = state.kpis.groups;
  const Icon = op ? stepIcon(op) : Play;
  const cases = state.kpis.caseCount;
  const step = state.steps.find((s) => s.op === op);
  const n = step?.remaining ?? step?.total ?? 0;
  const isForcedContain = op === 'contain' && step?.containMode !== 'NATURAL';

  const title =
    op === 'tickets'
      ? t('alt.confirm.ticketsTitle', {
          count: n,
          flights: t('count.flights', { count: cases }),
        })
      : op === 'acceptance'
        ? t('alt.confirm.acceptanceTitle', { count: n })
        : isForcedContain
          ? t('alt.confirm.containForcedTitle')
          : t('alt.confirm.containTitle');

  const body =
    op === 'tickets'
      ? t('alt.confirm.ticketsBody')
      : op === 'acceptance'
        ? t('alt.confirm.acceptanceBody')
        : isForcedContain
          ? t('alt.confirm.containForcedBody')
          : t('alt.confirm.containBody');

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        {g ? (
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground font-mono">
            {t('alt.confirm.facts', {
              assigned: g.assigned,
              rebookable: g.rebookable,
              ticketed: g.ticketed,
              held: g.hotelHeld,
            })}
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isFiring}>
            {t('common:common.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={isFiring} className="gap-2">
            {isFiring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 fill-current" />}
            {t('alt.confirm.execute')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
